// Dial pad orchestration (SPEC §19, 2026-10-01). One human press of Call (or Enter, or a recent number) runs:
// POST /api/calls/manual (find or create the number as my own lead) -> put that lead on the dialer screen -> dial(),
// which is the same gated /api/calls/prepare a queue lead goes through. A refusal is shown in plain words and
// nothing dials. After the call the normal wrap-up dock is on that lead, so outcome, notes, callback and booking
// save to it. 2026-10-02: "No campaign" (req.noCampaign) finds or creates the business only, and its wrap-up asks to
// save it to My leads or not; opts.callFromNumberId is the pad's "Call from" pick, for this call only.
// PJ 2026-10-20 ("I entered a number into the dialer to call and they didn't answer. I wanted to call back right
// away"): a number called recently, where only soft attempt rules refuse it, asks "Call this number again?" before it is
// opened; Yes opens it on the dialer and dials with the setter's yes, No leaves the pad as it was (with the amber note).
// PJ 2026-10-23: the pad dials the number typed. A typed store line of a lead that also has the owner's number dials the
// store line (target 'store'), not the owner's line Dial would call (padTarget). Review 2026-10-23: the server finds a
// lead by its owner's line or store line too (an owner's cell from Recent) and says which number was typed
// (ManualDialResponse.dialE164).
import type { GateReason, QueueItem } from '../../../src/shared/api';
import type { ManualDialRequest } from '../../../src/shared/api-manual';
import { ApiFailure, errorMessage } from '../api';
import { toast } from '../components/ui';
import { phone } from '../phone/manager';
import { endpoints, gateFromError } from './endpoints';
import { canLeaveLead } from './machine';
import { askRedial, checkPending, confirmableGate, dial, getDialer, openItem } from './store';
import type { DialTarget } from '../../../src/shared/api-numbers';
import { e164For, gateFor, padTarget } from './numbers';

export { padTarget };

export type PadOutcome =
  | { kind: 'dialing'; item: QueueItem; created: boolean }
  /** A gate refused it. `item` is set when the lead now exists (it stays in My leads), null when nothing was written. */
  | { kind: 'blocked'; reasons: GateReason[]; retryAt: string | null; businessName: string | null; item: QueueItem | null }
  /** Called recently (2026-10-20): "Call this number again?" is on screen. `ask` opens it again after a No. */
  | { kind: 'confirm'; item: QueueItem; created: boolean; ask: () => void }
  /** An earlier call still needs its outcome logged: the dialer screen shows it. */
  | { kind: 'owed'; message: string }
  | { kind: 'error'; message: string };

const OWED = 'Finish the current call and log its outcome first.';

/** Navigation is injected so this stays free of the app shell (and testable). opts.onDialing: the pad's own "now
 *  dialing" step (toast, clear, close), also run when a "Call this number again?" Yes starts the call later. */
export async function callFromPad(
  req: ManualDialRequest, go: (path: string) => void,
  opts: { callFromNumberId?: number | null; onDialing?: (out: Extract<PadOutcome, { kind: 'dialing' }>) => void } = {},
): Promise<PadOutcome> {
  if (!canLeaveLead(getDialer().call)) return { kind: 'owed', message: OWED };
  const why = phone.whyCantCall();
  if (why) return { kind: 'error', message: why };

  let res;
  try {
    res = await endpoints.manualDial(req);
  } catch (e) {
    const gate = gateFromError(e);
    if (gate) {
      const name = (gate as { businessName?: unknown }).businessName;
      return { kind: 'blocked', reasons: gate.reasons, retryAt: gate.retryAt ?? null, businessName: typeof name === 'string' ? name : null, item: null };
    }
    if (e instanceof ApiFailure && e.body.error === 'disposition_required') {
      await checkPending(); // brings the unlogged call up on the dialer
      go('/');
      return { kind: 'owed', message: e.body.message };
    }
    return { kind: 'error', message: errorMessage(e) };
  }

  const item = res.item;
  const created = res.businessCreated;
  const target = padTarget(item, res.dialE164 ?? null);
  const gate = gateFor(item, target);
  const confirm = confirmableGate(gate);
  if (confirm) {
    // Asked before anything opens: No changes nothing. Yes is the second human press that dials.
    const yes = () => {
      // PJ 2026-10-28: a Yes to "Meeting booked. Call anyway?" is the yes to the meeting too.
      void openAndDial(item, go, { callFromNumberId: opts.callFromNumberId ?? null, redialConfirmed: true, meetingConfirmed: confirm.meeting === true, target }).then((out) => {
        if (out.kind === 'dialing') opts.onDialing?.({ ...out, created });
        else if (out.kind === 'owed' || out.kind === 'error') toast(out.message, 'bad');
      });
    };
    const ask = () => askRedial(item, confirm, yes, e164For(item, target));
    ask();
    return { kind: 'confirm', item, created, ask };
  }
  if (!gate.ok) {
    return { kind: 'blocked', reasons: gate.reasons, retryAt: gate.retryAt ?? null, businessName: item.business.name, item };
  }
  const out = await openAndDial(item, go, { callFromNumberId: opts.callFromNumberId ?? null, target });
  return out.kind === 'dialing' ? { ...out, created } : out;
}

/** Put the lead on the dialer and dial it (the Call press, or a Yes, is the human action; prepare re-checks every gate
 *  and the picked number, and the dialer shows any refusal). */
async function openAndDial(item: QueueItem, go: (path: string) => void, d: { callFromNumberId: number | null; redialConfirmed?: boolean; meetingConfirmed?: boolean; target?: DialTarget | null }): Promise<PadOutcome> {
  const opened = await openItem(item);
  if (opened === 'busy') return { kind: 'owed', message: OWED };
  if (opened === 'failed') return { kind: 'error', message: getDialer().pendingError ?? "Couldn't open that lead." };
  go('/');
  void dial({
    callFromNumberId: d.callFromNumberId, ...(d.redialConfirmed ? { redialConfirmed: true } : {}),
    ...(d.meetingConfirmed ? { meetingConfirmed: true } : {}), ...(d.target ? { target: d.target } : {}),
  });
  return { kind: 'dialing', item, created: false };
}

/** Show a lead that exists (a blocked one, or one from My leads) on the dialer without dialing it. */
export async function openFromPad(item: QueueItem, go: (path: string) => void): Promise<boolean> {
  const opened = await openItem(item);
  if (opened === 'opened') go('/');
  return opened === 'opened';
}
